import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReservationServiceLine } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { CZK } from '../lib/format';

/**
 * The legacy edit form's "Doplňkové služby": a checklist of the catalogue
 * with a price box beside each tick, saved whole with Uložit. What the desk
 * ticks is what the booking carries afterwards; the server writes the
 * difference (added, removed, repriced) and journals each the legacy way.
 *
 * A done job keeps its tick — the person who did it is paid by that row —
 * and a service the catalogue has dropped is shown while ordered but
 * cannot be ticked anew. Services the catalogue does not offer for this
 * product are listed greyed, as the old form listed the whole catalogue.
 */
export function ServicesForm({
  reservationId,
  lines,
  onClose,
}: {
  reservationId: number;
  lines: ReservationServiceLine[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [ticked, setTicked] = useState<Set<number>>(new Set(lines.filter((l) => l.ordered).map((l) => l.id)));
  const [prices, setPrices] = useState<Record<number, string>>(
    Object.fromEntries(lines.map((l) => [l.id, String(l.price)])),
  );

  const m = useMutation({
    mutationFn: () =>
      api.setServices(reservationId, {
        services: lines
          .filter((l) => ticked.has(l.id))
          .map((l) => {
            const p = Number(prices[l.id]);
            return Number.isInteger(p) && p >= 0 ? { id: l.id, price: p } : { id: l.id };
          }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      onClose();
    },
  });

  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;

  const total = lines.filter((l) => ticked.has(l.id)).reduce((t, l) => t + (Number(prices[l.id]) || 0), 0);

  return (
    <form
      className="services-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!m.isPending) m.mutate();
      }}
    >
      <table className="mini">
        <tbody>
          {lines.map((l) => {
            const on = ticked.has(l.id);
            const locked = Boolean(l.done) || (!l.live && !on) || (!l.canOrder && !on);
            return (
              <tr key={l.id} className={!l.canOrder && !on ? 'muted' : undefined}>
                <td>
                  <label title={l.done ? 'Služba je už provedená' : !l.live ? 'Služba už není v nabídce' : !l.canOrder ? 'Pro tento produkt se nenabízí' : undefined}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={locked || m.isPending}
                      onChange={(e) => {
                        const next = new Set(ticked);
                        if (e.target.checked) next.add(l.id);
                        else next.delete(l.id);
                        setTicked(next);
                      }}
                    />{' '}
                    {l.title}
                    {l.done && <span className="arrived-mark"> ✔ hotovo</span>}
                  </label>
                </td>
                <td className="num">
                  {on ? (
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={65535}
                      step={1}
                      value={prices[l.id] ?? ''}
                      onChange={(e) => setPrices({ ...prices, [l.id]: e.target.value })}
                      disabled={m.isPending}
                      style={{ width: '6em' }}
                      aria-label={`Cena: ${l.title}`}
                    />
                  ) : (
                    <span className="empty">{l.listPrice > 0 ? `${CZK.format(l.listPrice)} Kč` : '—'}</span>
                  )}
                </td>
                <td>{on && <span className="empty">Kč</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="arrive-form">
        <span className="depart-owed">
          Služby celkem <strong>{CZK.format(total)} Kč</strong>
        </span>
        <button type="submit" className="btn primary" disabled={m.isPending}>
          {m.isPending ? 'Ukládám…' : 'Uložit'}
        </button>
        <button type="button" className="btn" onClick={onClose} disabled={m.isPending}>
          zrušit editaci
        </button>
        {message && <span className="arrive-error">{message}</span>}
      </div>
    </form>
  );
}
